/**
 * Lightweight conversion tracker for GA4 + Facebook Pixel.
 * Scripts are loaded by index.html (only after user consent — GDPR-safe).
 * This module just provides typed helpers for common funnel events.
 *
 * Usage:
 *   import { trackPurchase, trackBookingStart } from "../lib/tracking";
 *   trackPurchase({ booking_number: "EMA-12345", price_dkk: 199 });
 */

function _gtag(...args) {
  if (typeof window === "undefined") return;
  if (typeof window.gtag !== "function") return;
  try { window.gtag(...args); } catch { /* ignore */ }
}

function _fbq(...args) {
  if (typeof window === "undefined") return;
  if (typeof window.fbq !== "function") return;
  try { window.fbq(...args); } catch { /* ignore */ }
}

/** Booking flow started — user opened /book or clicked CTA */
export function trackBookingStart(meta = {}) {
  _gtag("event", "begin_checkout", { currency: "DKK", ...meta });
  _fbq("track", "InitiateCheckout", { currency: "DKK", ...meta });
}

/** User selected service tier + entered addresses */
export function trackBookingDetails(meta = {}) {
  _gtag("event", "add_shipping_info", { currency: "DKK", ...meta });
  _fbq("track", "AddPaymentInfo", { currency: "DKK", ...meta });
}

/** Successful paid booking (call from /thank-you page) */
export function trackPurchase({ booking_number, price_dkk, delivery_type } = {}) {
  const value = Number(price_dkk) || 0;
  _gtag("event", "purchase", {
    transaction_id: booking_number,
    value,
    currency: "DKK",
    items: delivery_type ? [{ item_name: delivery_type, price: value, quantity: 1 }] : undefined,
  });
  _fbq("track", "Purchase", { value, currency: "DKK", content_ids: booking_number ? [booking_number] : [] });
}

/** Contact form submitted (B2B lead) */
export function trackLead(meta = {}) {
  _gtag("event", "generate_lead", { currency: "DKK", ...meta });
  _fbq("track", "Lead", meta);
}

/** Driver job application submitted */
export function trackDriverApply(meta = {}) {
  _gtag("event", "sign_up", { method: "driver_apply", ...meta });
  _fbq("trackCustom", "DriverApply", meta);
}

/** Generic SPA page-view (call from router on route change) */
export function trackPageView(path) {
  if (typeof window === "undefined") return;
  _gtag("event", "page_view", { page_path: path || window.location.pathname });
  _fbq("track", "PageView");
}
